/**
 * Data access for the review entity.
 *
 * - ``GET  /api/products/<id>/reviews/`` — visible reviews of a product (ТЗ §9).
 * - ``POST /api/orders/<id>/review/``    — leave a review for a completed order (ТЗ §10-12).
 *
 * The product rating/count shown on cards comes denormalised on the product payload;
 * this list endpoint backs the product page's reviews section.
 */
import { apiClient, useApiQuery, type QueryState } from "@shared/api";

import type { Review, ReviewInput } from "./model";

/** Path of a product's visible-reviews endpoint. */
export function productReviewsPath(productId: number): string {
  return `/products/${productId}/reviews/`;
}

/** Path of an order's review-create endpoint. */
export function orderReviewPath(orderId: number): string {
  return `/orders/${orderId}/review/`;
}

/** Fetch the visible reviews of a product (most recent first, server-ordered). */
export function useProductReviews(
  productId: number | undefined,
): QueryState<readonly Review[]> {
  const enabled = productId !== undefined;
  return useApiQuery<readonly Review[]>(
    productId !== undefined ? productReviewsPath(productId) : "",
    { enabled, deps: [productId] },
  );
}

/** Create a review for a completed order. Returns the persisted review. */
export function postOrderReview(
  orderId: number,
  input: ReviewInput,
): Promise<Review> {
  return apiClient.post<Review, ReviewInput>(orderReviewPath(orderId), {
    body: input,
  });
}
